import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { contentApi } from '@/services/api'
import { ContentItem, ContentMetadata } from '@/types'
import toast from 'react-hot-toast'

export const useContent = () => {
  const queryClient = useQueryClient()

  // Get user's content with caching
  const useUserContent = (params?: any) => {
    // In development, check if user is authenticated via localStorage
    const isAuthenticated = import.meta.env.DEV 
      ? !!localStorage.getItem('cosmk_dev_user')
      : true // Assume authenticated in production (handled by auth context)

    return useQuery({
      queryKey: ['user-content', params],
      queryFn: () => contentApi.getContent(params),
      enabled: isAuthenticated,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
      retry: 2
    })
  }

  // Get content by ID
  const useContentById = (id: string) => {
    return useQuery({
      queryKey: ['content', id],
      queryFn: () => contentApi.getContentById(id),
      enabled: !!id,
      staleTime: 10 * 60 * 1000, // 10 minutes
      gcTime: 30 * 60 * 1000, // 30 minutes
    })
  }

  // Upload content mutation
  const useUploadContent = () => {
    return useMutation({
      mutationFn: ({ file, metadata, onProgress }: {
        file: File
        metadata: ContentMetadata
        onProgress?: (progress: number) => void
      }) => contentApi.uploadContent(file, metadata, onProgress),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-content'] })
        toast.success('Content uploaded successfully!')
      },
      onError: (error: any) => {
        toast.error(error.message || 'Upload failed')
      }
    })
  }

  // Update content mutation
  const useUpdateContent = () => {
    return useMutation({
      mutationFn: ({ id, data }: { id: string; data: Partial<ContentItem> }) =>
        contentApi.updateContent(id, data),
      onSuccess: (_, { id }) => {
        queryClient.invalidateQueries({ queryKey: ['content', id] })
        queryClient.invalidateQueries({ queryKey: ['user-content'] })
        toast.success('Content updated successfully!')
      },
      onError: (error: any) => {
        toast.error(error.message || 'Update failed')
      }
    }
    )
  }

  // Delete content mutation
  const useDeleteContent = () => {
    return useMutation({
      mutationFn: (id: string) => contentApi.deleteContent(id),
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['user-content'] })
        toast.success('Content deleted successfully!')
      },
      onError: (error: any) => {
        toast.error(error.message || 'Delete failed')
      }
    })
  }

  return {
    useUserContent,
    useContentById,
    useUploadContent,
    useUpdateContent,
    useDeleteContent
  }
}

export default useContent 